/**
 * PWA flush-and-reload sequences.
 *
 * Two exported entry points share one internal flush algorithm, differing ONLY
 * in whether they PURGE caches:
 *
 *   - applyWaitingUpdate() — the ROUTINE apply path (the UpdateBar tap + the
 *     silent invisible-apply paths in AutoUpdater). Posts plain SKIP_WAITING and
 *     PRESERVES the freshly-installed precache. See the WHY-NO-PURGE note below.
 *
 *   - purgeCachesAndReload() — the stale-chunk RESCUE path
 *     (stale-chunk-rescue.ts) ONLY. Posts PURGE_CACHES_AND_SKIP_WAITING and also
 *     nukes every cache + unregisters every SW client-side, because the app is
 *     already broken and a full re-install is the recovery.
 *
 * Shared flush algorithm:
 *
 * 1. Post the skip/purge message to the active SW controller and any
 *    waiting/installing registration so the SW activates.
 * 2. Race a controllerchange event against a 1500 ms timeout (iOS SA can stall
 *    on controllerchange if the SW never responds).
 * 3. (RESCUE ONLY) Client-side: clear all Cache Storage caches + unregister all
 *    SW registrations (belt-and-braces if the SW message round-trip stalled).
 * 4. Set sessionStorage["proof-update-reloading"]="1" so the next boot's
 *    AppLoader can render the re-warm message.
 * 5. Navigate via location.replace() with a unique _v= param so iOS Safari
 *    treats it as a fresh document request rather than a bfcache restore.
 *
 * Rule 8: steps 1–3 failures are logged via console.error; the final
 * location.replace() always fires so the user is never left stuck.
 *
 * Out of scope: IndexedDB (Yjs docs) and localStorage proof-* keys are
 * deliberately NOT cleared.
 */

export const RELOAD_FLAG_KEY = "proof-update-reloading";

type UpdateServiceWorkerFn = (reloadPage?: boolean) => Promise<void>;

/**
 * ROUTINE apply — activate the waiting SW and reload WITHOUT purging caches.
 *
 * WHY NO PURGE (2026-07-17 review fold-in)
 * ────────────────────────────────────────
 * A precache is only ever written during the SW `install` event
 * (`precacheAndRoute` is a no-op on activate). The waiting SW has ALREADY
 * populated its precache. Deleting all caches on a routine apply — as the
 * rescue path does — therefore strands the newly-activated SW with an EMPTY
 * precache that never re-installs.
 *
 * That failure is not hypothetical. vite-plugin-pwa's workbox-window runtime
 * carries its own unguarded `controlling → window.location.reload()` listener,
 * which can commit the reload BEFORE this flush's client-side unregister step
 * runs. The user then lands on a live SW whose precache we just wiped — offline
 * is silently dead until the next deploy installs a fresh SW.
 *
 * Routine cache turnover needs no purge: Workbox's `cleanupOutdatedCaches()`
 * drops the previous precache revision, and the SW activate handler purges the
 * navigation-HTML cache (sw.ts). We post plain SKIP_WAITING and reload, leaving
 * the new precache intact. The hard purge stays RESERVED for the rescue path
 * (purgeCachesAndReload) below.
 *
 * @param updateServiceWorkerFn — optional vite-plugin-pwa helper. When the
 *   SW-based `needRefresh` flag is set, pass `updateServiceWorker` from
 *   `useRegisterSW()` here so the plugin performs its own SKIP_WAITING dispatch
 *   alongside our direct postMessage.
 */
export async function applyWaitingUpdate(
  updateServiceWorkerFn?: UpdateServiceWorkerFn,
): Promise<void> {
  await flushAndReload({ purge: false, updateServiceWorkerFn });
}

/**
 * RESCUE path — hard-purge ALL caches + unregister every SW, then reload.
 *
 * RESERVED for stale-chunk-rescue.ts: an old cached shell is live against a new
 * precache and its lazy-chunk URLs 404. The app is already broken, so a full
 * nuke + fresh SW re-install (which re-populates the precache from scratch) is
 * the recovery — the precache-preservation concern that governs the routine
 * path (applyWaitingUpdate) does not apply because the current state is unusable
 * anyway. Do NOT call this from routine apply paths.
 *
 * @param updateServiceWorkerFn — optional vite-plugin-pwa helper. When the
 *   SW-based `needRefresh` flag is set, pass `updateServiceWorker` from
 *   `useRegisterSW()` here so the plugin performs its own SKIP_WAITING dispatch
 *   alongside our direct postMessage.
 */
export async function purgeCachesAndReload(
  updateServiceWorkerFn?: UpdateServiceWorkerFn,
): Promise<void> {
  await flushAndReload({ purge: true, updateServiceWorkerFn });
}

async function flushAndReload({
  purge,
  updateServiceWorkerFn,
}: {
  purge: boolean;
  updateServiceWorkerFn?: UpdateServiceWorkerFn | undefined;
}): Promise<void> {
  try {
    // Step 1: Tell the waiting SW to activate. Routine → plain SKIP_WAITING
    // (preserve the just-installed precache); rescue →
    // PURGE_CACHES_AND_SKIP_WAITING (nuke every cache SW-side first).
    const messageType = purge ? "PURGE_CACHES_AND_SKIP_WAITING" : "SKIP_WAITING";
    const postMsg = (target: ServiceWorker | null | undefined) => {
      target?.postMessage({ type: messageType });
    };

    postMsg(navigator.serviceWorker?.controller);

    // Also target any waiting registration explicitly (belt-and-braces).
    const reg = await navigator.serviceWorker?.getRegistration().catch((err: unknown) => {
      console.error("[proof SW] Reload flush (step 1 — getRegistration) failed:", err);
      return undefined;
    });
    if (reg) {
      postMsg(reg.waiting ?? reg.installing);
      // Also call the vite-plugin-pwa helper if provided — it posts
      // SKIP_WAITING and wires the controllerchange listener on its own side.
      if (updateServiceWorkerFn) {
        updateServiceWorkerFn(true).catch((err: unknown) => {
          console.error("[proof SW] updateServiceWorker failed:", err);
        });
      }
    }

    // Step 2: Race controllerchange against a 1500 ms timeout.
    await new Promise<void>((resolve) => {
      const timer = setTimeout(resolve, 1500);
      navigator.serviceWorker?.addEventListener(
        "controllerchange",
        () => {
          clearTimeout(timer);
          resolve();
        },
        { once: true },
      );
    });
  } catch (err) {
    // Steps 1–2 errors are logged but do not abort — proceed to navigation.
    console.error("[proof SW] Reload flush (steps 1-2) failed:", err);
  }

  // Step 3 (RESCUE ONLY): nuke all caches + unregister every SW. Skipped on the
  // routine path so the newly-installed precache survives — see WHY NO PURGE on
  // applyWaitingUpdate. Deleting caches here on a routine apply is exactly the
  // race that leaves the user on a live SW with an empty precache.
  if (purge) {
    try {
      // Step 3a: Clear all Cache Storage caches from the client side.
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((name) => caches.delete(name)));

      // Step 3b: Unregister all SW registrations.
      const registrations = (await navigator.serviceWorker?.getRegistrations()) ?? [];
      await Promise.all(registrations.map((r) => r.unregister()));
    } catch (err) {
      // Step 3 errors are logged but do not abort — navigate regardless.
      console.error("[proof SW] Reload flush (step 3 — cache clear) failed:", err);
    }
  }

  // Step 4: Set the re-warm flag so the next boot's AppLoader shows feedback.
  try {
    sessionStorage.setItem(RELOAD_FLAG_KEY, "1");
    // eslint-disable-next-line no-restricted-syntax -- best-effort: a re-warm UI hint flag, not user data; unavailable storage just skips the next-boot feedback (Rule 8 documented degradation).
  } catch {
    // sessionStorage may be unavailable (private browsing quota) — not critical.
  }

  // Step 5: Navigate with a unique _v= param. location.replace() removes the
  // current entry from the bfcache stack; the unique param forces iOS Safari
  // to treat it as a fresh request even in standalone mode.
  const sep = location.search ? "&" : "?";
  window.location.replace(location.pathname + location.search + sep + "_v=" + String(Date.now()));
}
